import {
  GetEventsQueryParams,
  GetCalendarServiceParams,
  GetCalendarServiceResponse,
  GetEventsResponse,
  CreateEventPayload,
  CreateEventResponse,
  AddSessionPayload,
  AddSessionResponse,
  PublishEventPayload,
  PublishEventResponse,
  GetEventResponse,
  AddPresentationToEventPayload,
  AddPresentationToEventResponse,
  AddLineupPayload,
  AddLineupResponse,
  GetEventReviewsParams,
  GetEventReviewsResponse,
  GetAgendaTypesResponse,
  GetAudioAllowed,
  AudioAllowed,
  GetEventPlaybooksResponse,
  GetEventPlaybookSelectionResponse,
  UpdateEventPlaybooksPayload,
  UpdateEventPlaybooksResponse,
} from "interfaces";
import instance from "lib/axiosInterceptor";

export async function getCalendar(
  getCalendarServiceParams: GetCalendarServiceParams,
): Promise<GetCalendarServiceResponse> {
  const params: GetCalendarServiceParams = {
    ...getCalendarServiceParams,
  };

  // Remove undefined values
  Object.keys(params).forEach((key) => {
    if (params[key as keyof GetCalendarServiceParams] === undefined) {
      delete params[key as keyof GetCalendarServiceParams];
    }
  });

  const response = await instance.get<GetCalendarServiceResponse>("/Calendar", {
    params,
  });

  return response.data;
}

export async function getEvents(
  getEventsParams: GetEventsQueryParams,
): Promise<GetEventsResponse> {
  const params: GetEventsQueryParams = {
    ...getEventsParams,
  };

  // Remove undefined values
  Object.keys(params).forEach((key) => {
    if (params[key as keyof GetCalendarServiceParams] === undefined) {
      delete params[key as keyof GetCalendarServiceParams];
    }
  });

  const response = await instance.get<GetEventsResponse>("/events", {
    params,
  });
  return response.data;
}

export async function createEventService(
  payload: CreateEventPayload,
): Promise<CreateEventResponse> {
  const formData = new FormData();
  formData.append("title", payload.title);
  formData.append("description", payload.description);
  formData.append("startDate", payload.startDate);
  formData.append("teamId", payload.teamId);
  formData.append("EventLocation.FullAddress", payload.location || "");
  formData.append("EventLocation.Latitude", payload.latitude || "");
  formData.append("EventLocation.Longitude", payload.longitude || "");
  if (payload?.quickPresentation) {
    formData.append("QuickPresentation", String(payload?.quickPresentation));
  }
  if (payload.placeId) {
    formData.append("EventLocation.PlaceId", payload.placeId);
  }
  if (payload.eventLink) {
    formData.append("eventLink[0].app", payload.eventLink.app);
    formData.append("eventLink[0].link", payload.eventLink.link);
  }
  if (payload.bannerUrl) {
    formData.append("BannerUrl", payload.bannerUrl);
  }

  const response = await instance.post<CreateEventResponse>(
    "/Events/Create",
    formData,
  );
  return response.data;
}

export async function addSessionService(
  payload: FormData,
): Promise<AddSessionResponse> {
  const response = await instance.post<AddSessionResponse>(
    "/Events/AddSession",
    payload,
  );
  return response.data;
}

export async function publishEventService(
  payload: PublishEventPayload,
): Promise<PublishEventResponse> {
  const response = await instance.post<PublishEventResponse>(
    `/Events/Publish/${payload.eventId}?teamId=${payload.teamId}`,
  );
  return response.data;
}

export async function getEventService(
  eventId: string,
): Promise<GetEventResponse> {
  const response = await instance.get<GetEventResponse>(`/Events/${eventId}`);
  return response.data;
}

export async function addPresentationToEventService(
  payload: AddPresentationToEventPayload,
): Promise<AddPresentationToEventResponse> {
  const response = await instance.post<AddPresentationToEventResponse>(
    "/Events/Collaborator/AddPresentation",
    payload,
  );
  return response.data;
}

export async function addLineupService(
  payload: AddLineupPayload,
): Promise<AddLineupResponse> {
  const response = await instance.post<AddLineupResponse>(
    `/Events/Lineups/Add/${payload.eventId}?teamId=${payload.teamId}`,
    payload.formData,
  );
  return response.data;
}

export async function getEventReviews(
  params: GetEventReviewsParams,
): Promise<GetEventReviewsResponse> {
  const queryParams: GetEventReviewsParams = {
    ...params,
  };

  // Remove undefined values
  Object.keys(queryParams).forEach((key) => {
    if (queryParams[key as keyof GetEventReviewsParams] === undefined) {
      delete queryParams[key as keyof GetEventReviewsParams];
    }
  });

  const response = await instance.get<GetEventReviewsResponse>(
    "/Events/Reviews",
    {
      params: queryParams,
    },
  );

  return response.data;
}

export async function getAgendaTypes(): Promise<GetAgendaTypesResponse> {
  const response =
    await instance.get<GetAgendaTypesResponse>(`/Events/AgendaTypes`);
  return response.data;
}

/** The full playbook catalogue, keyed by playbook code. */
export async function getEventPlaybooksService(): Promise<GetEventPlaybooksResponse> {
  const response =
    await instance.get<GetEventPlaybooksResponse>("/Events/Playbooks");
  return response.data;
}

/** The playbooks already picked for a given event. */
export async function getEventPlaybookSelectionService(
  eventId: string,
): Promise<GetEventPlaybookSelectionResponse> {
  const response = await instance.get<GetEventPlaybookSelectionResponse>(
    `/Events/Playbooks/${eventId}`,
  );
  return response.data;
}

export async function updateEventPlaybooksService(
  payload: UpdateEventPlaybooksPayload,
): Promise<UpdateEventPlaybooksResponse> {
  const response = await instance.put<UpdateEventPlaybooksResponse>(
    "/Events/Playbooks",
    payload,
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data;
}

export const questionAllowed = async (
  payload: GetAudioAllowed,
): Promise<AudioAllowed> => {
  const response = await instance.get(
    `/Document/AudioAllowed?eventId=${payload.eventId}&presentationId=${payload.presentationId}&presentaConnectionId=${payload.presentaConnectionId}`,
  );
  return response.data;
};
